import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { organisations } from "@/api/client";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { StatGrid } from "@/components/StatGrid";
import { orgQk, useOrganisations, type OrganisationRow } from "@/modules/organisations/data";
import { Select } from "@/components/inputs";

/** Organisations — every party on the platform.
 *
 *  REAL DATA. Dattatray: *"Batterysmart CMP-004 … Nexcell Pack Works CMP-011
 *  … Why there are these organisations?"* They did not exist. Eighteen
 *  companies were hardcoded in `fixtures.ts` for the Fabric UI port, and this
 *  page rendered them for weeks. The database holds three.
 *
 *  AN ORGANISATION IS A PARTY (D-80): the same row is a customer, a
 *  manufacturer, a repairer or a supplier depending on which facets it wears.
 *  `is_manufacturer` is shown here because D-93 retires the separate
 *  Manufacturers sub-page — one organisation should not be edited in two
 *  places, which is how the live data ended up with "Aphelion Energy Pvt Ltd"
 *  in one table and "Aphelion Energy" in the other.
 *
 *  SCOPED IN SQL. A customer's query cannot return another customer's row, so
 *  this list is the whole platform only for a platform operator.
 */
const COLS: DataCol<OrganisationRow>[] = [
  { key: "name", label: "Organisation", width: 200, render: (o) => o.name },
  { key: "id", label: "ID", width: 100, mono: true, render: (o) => o.organisation_id },
  {
    key: "facets", label: "", width: 130,
    render: (o) => o.is_manufacturer ? <Chip tone="info">manufacturer</Chip> : null,
  },
  { key: "plan", label: "Plan", width: 100, render: (o) => o.plan },
  {
    key: "ents", label: "Products", width: 220,
    render: (o) => o.entitlements.length
      ? o.entitlements.join(", ")
      : <span className="cap">none allocated</span>,
  },
  { key: "users", label: "People", width: 80, render: (o) => o.users },
  {
    key: "status", label: "Status", width: 100,
    render: (o) => o.status === "active"
      ? <Chip tone="ok">active</Chip>
      : <Chip tone="mute">{o.status}</Chip>,
  },
];

export function OrganisationsPage() {
  const q = useOrganisations();
  const nav = useNavigate();
  const { can } = useSession();
  // SUPER ADMIN ONLY. Dattatray: "Only for super admin (add organisation and
  // Add people)." Creating one MINTS A PARTY other companies can ship to and
  // see in a picker, so this capability is in no plan and no company role.
  const mayAuthor = can(CAP.MANAGE_ORGANISATIONS);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);

  const all = q.data?.organisations ?? [];
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((o) => o.name.toLowerCase().includes(needle)
      || o.organisation_id.toLowerCase().includes(needle));
  }, [all, search]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations"
        title={q.data?.scoped ? "Your organisation" : "Organisations"}
        desc="Every party on the platform. The same row is a customer, a manufacturer or a supplier depending on the facets it wears."
      />

      <StatGrid stats={[
        { k: "Organisations", v: all.length },
        { k: "People", v: all.reduce((n, o) => n + o.users, 0) },
        { k: "Manufacturers", v: all.filter((o) => o.is_manufacturer).length },
      ]} />

      <div style={{ display: "flex", gap: 10, margin: "10px 0", alignItems: "center" }}>
        <SearchInput value={search} placeholder="Search organisations…"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
        {mayAuthor && (
          <button className={adding ? "btn ghost sm" : "btn teal sm"}
            style={{ marginLeft: "auto" }} onClick={() => setAdding(!adding)}>
            {adding ? "Cancel" : "Add organisation"}
          </button>
        )}
      </div>

      {adding && <AddOrganisationRow onDone={() => setAdding(false)} />}

      <DataTable rows={rows} cols={COLS} rowKey={(o) => o.organisation_id}
        onRowClick={(o) => nav(`/organisations/organisations/${o.organisation_id}`)}
        empty={<div className="panel panel-pad cap">
          {q.isLoading ? "Loading…" : "No organisations match."}
        </div>} />
    </div>
  );
}


/** Inline, like everything else that adds a row (Dattatray: "No Pop up").
 *
 *  CREATING AN ORGANISATION MINTS A PARTY (D-80): one `tenants` row that can
 *  be a customer, a manufacturer, a repairer or a supplier depending on the
 *  facets it later wears. That is why it is super-admin only, and why a
 *  DUPLICATE NAME is refused rather than created beside the first — name is
 *  the only thing an operator can match a counterparty on, and the live data
 *  already shows what the alternative costs ("Aphelion Energy Pvt Ltd" in one
 *  table, "Aphelion Energy" in the other).
 */
function AddOrganisationRow({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("basic");

  const add = useMutation({
    mutationFn: () => organisations.create({ name, plan }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgQk.list });
      onDone();
    },
  });

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Name" hint="must be unique — it is how a person matches a counterparty">
        <SearchInput value={name} autoFocus placeholder="Nexcell Pack Works"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
      <ScopeField label="Plan" hint="what they may open; billing moves it later">
        <Select value={plan} onChange={(e) => setPlan(e.target.value)}>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
          <option value="pro_plus">Pro+</option>
        </Select>
      </ScopeField>
      <ScopeAction>
        <button className="btn teal sm" disabled={!name.trim() || add.isPending}
          onClick={() => add.mutate()}>
          {add.isPending ? "Creating…" : "Create organisation"}
        </button>
      </ScopeAction>
      {add.isError && (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(add.error as Error).message}
        </div>
      )}
    </div>
  );
}
